import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import { superAdminTooltips } from '../../../shared/config/actionHelpCatalog';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import {
  ADMIN_COMPANY_PLANS,
  ADMIN_COMPANY_STATUSES,
  formatCompanyBulkResult,
  formatCascadeCounts,
} from '../lib/adminCompanies';
import type {
  AdminCompanyBulkAction,
  AdminCompanyBulkOutcome,
  AdminCompanyDetailDto,
  AdminCompanyDto,
  AdminCompanyStatus,
} from '../types';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  AdminButton,
  AdminDataTable,
  ConfirmDialog,
  StatusPill,
  useAdminToast,
  type AdminDataTableBulkContext,
  type AdminDataTableColumn,
  type AdminDataTableFilterValues,
  type AdminDataTableRowId,
  type AdminDataTableSortState,
} from './components';
import './styles/adminCompanies.css';

export interface CompanyTableProps {
  companies: readonly AdminCompanyDto[];
  detailById?: (id: number) => AdminCompanyDetailDto | null;
  onCreate?: () => void;
  onOpen?: (company: AdminCompanyDto) => void;
  onBulkAction?: (
    action: AdminCompanyBulkAction,
    ids: number[],
    expectedCounts?: Record<number, { users: number; licenses: number }>
  ) => Promise<AdminCompanyBulkOutcome>;
  onPrepareBulkDelete?: (ids: number[]) => Promise<Record<number, { users: number; licenses: number }>>;
  filterValues?: AdminDataTableFilterValues;
  onFilterValuesChange?: (filters: AdminDataTableFilterValues) => void;
  sort?: AdminDataTableSortState;
  onSortChange?: (sort: AdminDataTableSortState) => void;
  selectedIds?: readonly AdminDataTableRowId[];
  onSelectedIdsChange?: (ids: readonly AdminDataTableRowId[]) => void;
  busy?: boolean;
}

const PlanFilterOptions = ADMIN_COMPANY_PLANS.map((plan) => ({ value: plan, label: plan }));
const StatusFilterOptions = ADMIN_COMPANY_STATUSES.map((status) => ({
  value: status,
  label: status.charAt(0).toUpperCase() + status.slice(1),
}));

const titleCase = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const statusTone = (status: AdminCompanyStatus) => status === 'active'
  ? 'success' as const
  : status === 'suspended' ? 'critical' as const : 'warning' as const;

const planTone = (plan: AdminCompanyDto['plan']) => plan === 'Enterprise'
  ? 'accent' as const
  : plan === 'Team' ? 'info' as const : 'warning' as const;

const ColumnHelp: React.FC<{
  label: string;
  help: Parameters<typeof HelpTooltipTrigger>[0]['help'];
}> = ({ label, help }) => (
  <HelpTooltipTrigger label={label} help={help} placement="bottom-start" />
);

const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(date)
    : 'Unknown';
};

const createColumns = (): AdminDataTableColumn<AdminCompanyDto>[] => [
  {
    id: 'name', header: 'Company', accessor: (company) => company.name,
    headerAccessory: <ColumnHelp label="Company" help={superAdminTooltips.companyNameColumn} />,
    render: (company) => <strong className="admin-company-name">{company.name}</strong>,
    filter: { type: 'text', placeholder: 'Filter company' }, width: '1.2fr',
  },
  {
    id: 'domain', header: 'Domain', accessor: (company) => company.domain,
    headerAccessory: <ColumnHelp label="Domain" help={superAdminTooltips.companyLicenseKeysColumn} />,
    render: (company) => <span className="admin-company-domain">{company.domain}</span>,
    filter: { type: 'text', placeholder: 'Filter domain' }, width: '1.1fr',
  },
  {
    id: 'plan', header: 'Plan', accessor: (company) => company.plan,
    render: (company) => <StatusPill tone={planTone(company.plan)}>{company.plan}</StatusPill>,
    filter: { type: 'select', options: PlanFilterOptions, allLabel: 'All plans' }, width: '.75fr',
  },
  {
    id: 'users', header: 'Users', accessor: (company) => company.usersCount,
    headerAccessory: <ColumnHelp label="Users" help={superAdminTooltips.companyUsersColumn} />,
    numeric: true, width: '.6fr',
  },
  {
    id: 'licenses', header: 'Licenses', accessor: (company) => company.licensesCount,
    headerAccessory: <ColumnHelp label="Licenses" help={superAdminTooltips.companyLicensesColumn} />,
    numeric: true, width: '.7fr',
  },
  {
    id: 'country', header: 'Country', accessor: (company) => company.country,
    filter: { type: 'text', placeholder: 'Filter country' }, width: '.9fr',
  },
  {
    id: 'created', header: 'Created', accessor: (company) => company.createdAt,
    sortValue: (company) => Date.parse(company.createdAt),
    render: (company) => formatDate(company.createdAt), numeric: true, width: '.85fr',
  },
  {
    id: 'status', header: 'Status', accessor: (company) => company.status,
    render: (company) => <StatusPill tone={statusTone(company.status)}>{titleCase(company.status)}</StatusPill>,
    filter: { type: 'select', options: StatusFilterOptions, allLabel: 'All statuses' }, width: '.75fr',
  },
];

interface PendingDelete {
  ids: number[];
  preview: string;
  expectedCounts: Record<number, { users: number; licenses: number }>;
  deselectIds: AdminDataTableBulkContext<AdminCompanyDto>['deselectIds'];
}

const CompanyTableHeader: React.FC<{ busy: boolean; count: number; onCreate?: () => void }> = ({
  busy,
  count,
  onCreate,
}) => (
  <header className="admin-companies__header">
    <h2>Companies <span aria-hidden="true">{count}</span></h2>
    <AdminButton variant="primary" disabled={!onCreate || busy} onClick={onCreate}>
      <AddIcon aria-hidden="true" fontSize="small" /> New company
    </AdminButton>
  </header>
);

const CompanyTable: React.FC<CompanyTableProps> = ({
  companies,
  detailById,
  onCreate,
  onOpen,
  onBulkAction,
  onPrepareBulkDelete,
  filterValues,
  onFilterValuesChange,
  sort,
  onSortChange,
  selectedIds,
  onSelectedIdsChange,
  busy = false,
}) => {
  const { showToast } = useAdminToast();
  const [pendingDelete, setPendingDelete] = React.useState<PendingDelete | null>(null);
  const [bulkSummary, setBulkSummary] = React.useState<string | null>(null);
  const columns = React.useMemo(createColumns, []);

  const runBulk = async (
    action: AdminCompanyBulkAction,
    ids: number[],
    deselectIds: PendingDelete['deselectIds'],
    expectedCounts?: PendingDelete['expectedCounts']
  ) => {
    if (!onBulkAction) {
      return;
    }
    try {
      const result = action === 'delete'
        ? await onBulkAction(action, ids, expectedCounts)
        : await onBulkAction(action, ids);
      deselectIds(result.succeeded);
      const refresh = result.refreshError
        ? ` Canonical refresh failed (${result.refreshError.code}).`
        : '';
      const message = `${formatCompanyBulkResult(action, result)}${refresh}`;
      setBulkSummary(message);
      showToast(message, { tone: result.failed.length > 0 || result.refreshError ? 'warning' : 'success' });
    } catch (error) {
      const failure = getAdminApiError(error, 'company_bulk_failed');
      const message = `Bulk action failed (${failure.code}).`;
      setBulkSummary(message);
      showToast(message, { tone: 'critical' });
    }
  };

  const bulkSlot = ({ selectedRows, deselectIds }: AdminDataTableBulkContext<AdminCompanyDto>) => {
    const ids = selectedRows.map((company) => company.id);
    const fallbackCounts = Object.fromEntries(selectedRows.map((company) => {
      const detail = detailById?.(company.id);
      return [company.id, detail?.deletePreview ?? { users: company.usersCount, licenses: company.licensesCount }];
    }));
    const openDelete = async () => {
      try {
        const expectedCounts = onPrepareBulkDelete
          ? await onPrepareBulkDelete(ids)
          : fallbackCounts;
        const preview = Object.values(expectedCounts).map(formatCascadeCounts).join('; ');
        setPendingDelete({ ids, preview, expectedCounts, deselectIds });
      } catch (error) {
        const failure = getAdminApiError(error, 'company_detail_failed');
        showToast(`Delete preview failed (${failure.code}).`, { tone: 'critical' });
      }
    };
    return (
      <>
        <span className="admin-company-bulk-count"><strong>{ids.length}</strong> selected</span>
        <AdminButton disabled={busy || !onBulkAction} onClick={() => void runBulk('suspend', ids, deselectIds)}>Suspend</AdminButton>
        <AdminButton variant="danger" disabled={busy || !onBulkAction} onClick={() => void openDelete()}>Delete</AdminButton>
      </>
    );
  };

  return (
    <section className="admin-companies" data-ready={String(!busy)} data-testid="admin-companies">
      <CompanyTableHeader busy={busy} count={companies.length} onCreate={onCreate} />
      <p className="admin-company-suspension-note">
        Suspension blocks company users and licenses without deleting or rewriting child records.
      </p>
      {bulkSummary && <div className="admin-company-bulk-summary" aria-live="polite">{bulkSummary}</div>}
      <AdminDataTable
        ariaLabel="Companies"
        rows={companies}
        columns={columns}
        getRowId={(company) => company.id}
        getRowLabel={(company) => company.name}
        selectable
        onRowActivate={busy ? undefined : onOpen}
        bulkSlot={bulkSlot}
        emptyState="Nothing matches the current company filters"
        filterValues={filterValues}
        onFilterValuesChange={onFilterValuesChange}
        sort={sort}
        onSortChange={onSortChange}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete selected companies?"
        description={`The server will confirm the exact cascade before deleting. Current preview: ${pendingDelete?.preview ?? 'no children'}.`}
        confirmLabel={`Delete ${pendingDelete?.ids.length ?? 0} ${pendingDelete?.ids.length === 1 ? 'company' : 'companies'}`}
        confirmDisabled={busy}
        closeOnConfirm={false}
        onConfirm={async () => {
          if (!pendingDelete) {
            return;
          }
          await runBulk('delete', pendingDelete.ids, pendingDelete.deselectIds, pendingDelete.expectedCounts);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </section>
  );
};

export default CompanyTable;
